import { forwardRef, type Ref, type CSSProperties } from 'react'
import classNames from 'classnames'
import { getAriaLabel } from 'shared/lib'
import icons, { type IIconsVector } from './icons'
import styles from './styles.module.scss'

export type IIconProps = {
  icon?: IIconsVector
  customIcon?: JSX.Element
  count?: number
  countIndicator?: boolean
  fontSize?: number
  insideBox?: boolean
  boxRounded?: boolean
  tertiary?: boolean
  color?: string
  animation?: boolean
  padding?: number | string
  className?: string
  disabled?: boolean
  ariaLabel?: string
}

export const IconRef = (props: IIconProps, ref: Ref<HTMLDivElement>) => {
  const icon = props.icon || 'logoSmallBlue'
  const fontSize = props.fontSize || 16
  const count = props.count && (props.count > 99 ? '99+' : props.count)
  const color = props.color
  const padding = props.padding
  const stylesProps: CSSProperties = {
    fontSize,
    padding,
    boxSizing: 'content-box',
  }
  const iconAriaLabel = `Icon_${props.ariaLabel || ''}`

  if (props.color) stylesProps.color = `${color}`

  const renderIndicator = () => {
    if (props.countIndicator && count) {
      return (
        <div
          className={classNames(styles.count, styles.count__indicator)}
          aria-label={getAriaLabel(iconAriaLabel, 'count')}
        />
      )
    }

    if (count) {
      return (
        <div className={styles.count} aria-label={getAriaLabel(iconAriaLabel, 'count')}>
          {count}
        </div>
      )
    }

    return null
  }

  return (
    <div
      ref={ref}
      className={classNames(
        styles.wrap,
        {
          [styles.tertiary]: props.tertiary,
          [styles.box]: props.insideBox,
          [styles.boxRounded]: props.boxRounded,
        },
        props.className
      )}
      style={stylesProps}
      aria-label={getAriaLabel(iconAriaLabel)}
    >
      {renderIndicator()}

      <div
        className={classNames(
          styles.icon,
          props.animation && styles.animation,
          props.disabled && styles.disabled
        )}
        aria-label={getAriaLabel(iconAriaLabel, 'content')}
      >
        {props.customIcon || icons[icon]}
      </div>
    </div>
  )
}

export const Icon = forwardRef(IconRef)
